import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ApiError } from "./lib/api";
import { SessionProvider, useSession } from "./lib/session";
import { Loading } from "./components/ui";
import { Allowed, Layout } from "./components/Layout";
import { ErrorBoundary, RootErrorBoundary, reloadForNewBuild } from "./components/ErrorBoundary";
import { rememberQueries } from "./lib/smooth";
import "./styles.css";
import "./lib/staging";
import "./lib/report";

// All screens ship in one bundle. Route-level code splitting saved little
// for an app used all day, and it broke open tabs after every deploy (the
// old chunks are gone), so screens are imported directly.
import { Login, Invite, Forgot, GoogleDone, Home, SupportDone } from "./routes/auth";
import Dashboard from "./routes/dashboard";
import { Contacts, ContactPage } from "./routes/contacts";
import Tasks from "./routes/tasks";
import Inbox from "./routes/inbox";
import { Agents, AgentPage, AgentNew } from "./routes/agents";
import Knowledge from "./routes/knowledge";
import Team from "./routes/team";
import Channels from "./routes/channels";
import Templates from "./routes/templates";
import Results from "./routes/results";
import Calendar from "./routes/calendar";
import BookingSetupPage from "./routes/bookingSetup";
import { Deals, DealPage, Pipelines } from "./routes/deals";
import { Settings, SidebarSettings, UsageSettings, SupportSettings } from "./routes/settings";
import WorkspaceSettings from "./routes/workspace";
import { AutomationPage, Automations } from "./routes/automations";
import { BrandSettingsPage, BookingPageSettings } from "./routes/brandPage";
import { EmailSettings } from "./routes/emailSettings";
import { ImportList, ImportPage } from "./routes/import";
import { AccountSecurity, OrgSecuritySettings } from "./routes/security";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      // Pages opened earlier show straight away from memory and refresh quietly.
      gcTime: 30 * 60_000,
      refetchOnWindowFocus: true,
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
    },
  },
});

rememberQueries(queryClient);

function Protected({ children }: { children: React.ReactNode }) {
  const { status } = useSession();
  if (status === "checking") return <Loading />;
  if (status === "signed_out") return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function App() {
  return (
    <ErrorBoundary>
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/invite" element={<Invite />} />
        <Route path="/forgot" element={<Forgot />} />
        <Route path="/auth/google" element={<GoogleDone />} />
        <Route path="/support" element={<SupportDone />} />
        <Route path="/" element={<Protected><Home /></Protected>} />
        <Route path="/account/security" element={<Protected><AccountSecurity /></Protected>} />
        <Route path="/o/:orgId" element={<Protected><Layout /></Protected>}>
          <Route index element={<Dashboard />} />
          <Route path="results" element={<Allowed to="contacts_all"><Results /></Allowed>} />
          <Route path="calendar" element={<Calendar />} />
          <Route path="services" element={<Allowed to="bookings"><BookingSetupPage /></Allowed>} />
          <Route path="calendar/setup" element={<Navigate to="../services" replace />} />
          <Route path="contacts" element={<Contacts />} />
          <Route path="contacts/import" element={<Allowed to="import"><ImportList /></Allowed>} />
          <Route path="contacts/import/:importId" element={<Allowed to="import"><ImportPage /></Allowed>} />
          <Route path="contacts/:contactId" element={<ContactPage />} />
          <Route path="tasks" element={<Tasks />} />
          {/* One route, so opening a conversation doesn't rebuild the inbox. */}
          <Route path="inbox/:threadId?" element={<Inbox />} />
          <Route path="agents" element={<Allowed to="agents"><Agents /></Allowed>} />
          <Route path="agents/new" element={<Allowed to="agents"><AgentNew /></Allowed>} />
          <Route path="agents/:agentId" element={<Allowed to="agents"><AgentPage /></Allowed>} />
          <Route path="knowledge" element={<Allowed to="knowledge"><Knowledge /></Allowed>} />
          <Route path="team" element={<Allowed to="team"><Team /></Allowed>} />
          <Route path="settings" element={<Settings />} />
          <Route path="settings/workspace" element={<Allowed to="workspace"><WorkspaceSettings /></Allowed>} />
          <Route path="settings/brand" element={<Allowed to="brand"><BrandSettingsPage /></Allowed>} />
          <Route path="booking-page" element={<Allowed to="brand"><BookingPageSettings /></Allowed>} />
          <Route path="settings/email" element={<Allowed to="email"><EmailSettings /></Allowed>} />
          <Route path="settings/security" element={<Allowed to="team"><OrgSecuritySettings /></Allowed>} />
          <Route path="settings/account" element={<AccountSecurity embedded />} />
          <Route path="settings/sidebar" element={<SidebarSettings />} />
          <Route path="settings/usage" element={<Allowed to="team"><UsageSettings /></Allowed>} />
          <Route path="settings/support" element={<SupportSettings />} />
          <Route path="channels" element={<Allowed to="agents"><Channels /></Allowed>} />
          <Route path="channels/:connectionId/templates" element={<Allowed to="agents"><Templates /></Allowed>} />
          <Route path="automations" element={<Allowed to="automations"><Automations /></Allowed>} />
          <Route path="automations/new" element={<Allowed to="automations"><AutomationPage /></Allowed>} />
          <Route path="automations/:automationId" element={<Allowed to="automations"><AutomationPage /></Allowed>} />
          <Route path="deals" element={<Deals />} />
          <Route path="deals/pipelines" element={<Allowed to="pipelines"><Pipelines /></Allowed>} />
          <Route path="deals/:dealId" element={<DealPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

// Vite reports chunks that fail to preload after a deploy; reload to get the new build.
// The error still reaches the screen's error boundary, which shows "SVENEX
// was updated" for the moment before the reload lands.
window.addEventListener("vite:preloadError", () => {
  reloadForNewBuild();
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <RootErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionProvider>
          <App />
        </SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
    </RootErrorBoundary>
  </StrictMode>,
);
